import { useValue } from "@legendapp/state/react";
import { getPlanLabel } from "@repo/shared/billing/plans";
import { Icon } from "@repo/ui/components/icon";
import { cn } from "@repo/ui/lib/utils";
import { Button } from "@repo/ui/ui/button";
import { Progress, ProgressLabel } from "@repo/ui/ui/progress";
import { SidebarFooter } from "@repo/ui/ui/sidebar";
import { useEffect, useState } from "react";

import { UserHeader } from "~/components/header/user-header";
import { ablyClient } from "~/libs/pubsub.lib";
import { subscription$ } from "~/stores/subscription";
import { GetStartedChecklist } from "./get-started-checklist";

const NEW_VERSION_EVENT = "new-version";
const APP_CHANNEL = "app";
const DAY_MS = 1_000 * 60 * 60 * 24;

/**
 * Shows the plan trial progress bar. Stays visible for the whole pre-payment
 * lifecycle — a progress bar with days left while trialing, then a "Trial
 * ended" state once the trial lapses — and only disappears once the org pays
 * (subscription status `active`).
 */
const TrialBadge = () => {
  const trial = useValue(() => {
    const sub = subscription$.get();
    // Hide once paid (active) or when there's no subscription at all.
    if (!sub || sub.status === "active") return null;

    const end = sub.trialEnd ? new Date(sub.trialEnd).getTime() : null;
    const start = sub.trialStart
      ? new Date(sub.trialStart).getTime()
      : end != null
        ? end - 14 * DAY_MS
        : null;

    // No trial dates: still surface an ended state so the prompt persists.
    if (end == null || start == null) {
      return { plan: sub.plan, daysLeft: 0, progress: 100, ended: true };
    }

    const total = Math.max(end - start, DAY_MS);
    const elapsed = Math.min(Math.max(Date.now() - start, 0), total);
    const daysLeft = Math.max(0, Math.ceil((end - Date.now()) / DAY_MS));

    return {
      plan: sub.plan,
      daysLeft,
      progress: (elapsed / total) * 100,
      ended: sub.status !== "trialing" || daysLeft <= 0,
    };
  });

  if (!trial) return null;

  return (
    <div className="flex flex-col">
      <Progress
        value={trial.progress}
        className="gap-1 px-3.5 pb-2 **:data-[slot=progress-indicator]:bg-muted-foreground dark:**:data-[slot=progress-indicator]:bg-primary"
      >
        <div className="flex h-8 w-full items-center justify-between">
          <ProgressLabel className="-ml-1 text-muted-foreground text-xs">
            {getPlanLabel(trial.plan)} plan trial
          </ProgressLabel>
          <span className="ml-auto text-muted-foreground text-xs/relaxed tabular-nums">
            {trial.ended
              ? "Trial ended"
              : `${trial.daysLeft} ${trial.daysLeft === 1 ? "day" : "days"} left`}
          </span>
        </div>
      </Progress>
    </div>
  );
};

const UpdateBanner = () => {
  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        className="h-auto flex-1 justify-start gap-2 rounded-sm px-2 py-1.5 text-xs"
        onClick={() => window.location.reload()}
      >
        <Icon name="arrows-clockwise-repeat" className="size-3.5" />
        <span>Update available &mdash; reload page.</span>
      </Button>
    </div>
  );
};

export const AppSidebarFooter = () => {
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const channel = ablyClient.channels.get(APP_CHANNEL);
    const handler = () => setHasNewVersion(true);

    channel.subscribe(NEW_VERSION_EVENT, handler);

    return () => {
      channel.unsubscribe(NEW_VERSION_EVENT, handler);
    };
  }, []);

  useEffect(() => {
    if (hasNewVersion) {
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    }
  }, [hasNewVersion]);

  return (
    <SidebarFooter>
      {hasNewVersion && (
        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-300 ease-out",
            visible ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          <div className="overflow-hidden">
            <UpdateBanner />
          </div>
        </div>
      )}
      <GetStartedChecklist />
      <TrialBadge />
      <UserHeader />
    </SidebarFooter>
  );
};
